import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const carnavalDeNice: ChauffeurPage = {
  slug: "carnaval-de-nice",
  title: "Chauffeur privé Carnaval de Nice – VTC familles & groupes",
  subtitle:
    "Chauffeur privé pendant le Carnaval de Nice : transferts hôtel et aéroport malgré les fermetures de la Promenade, dépose au plus près des défilés et excursions.",
  keywords: [
    "chauffeur carnaval de nice",
    "vtc carnaval de nice",
    "chauffeur privé carnaval nice",
  ],
  hero: {
    title: "Chauffeur privé pendant le Carnaval de Nice",
    subtitle:
      "En février, l'un des plus grands carnavals du monde ferme la Promenade et la Place Masséna : un chauffeur qui contourne barrières et foule pour familles et groupes.",
    keywords: [
      "chauffeur Carnaval de Nice",
      "VTC Promenade des Anglais",
      "chauffeur Place Masséna",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Carnaval de Nice",
    badges: [
      "Fermetures Promenade maîtrisées",
      "Dépose près des défilés",
      "Familles & groupes",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Carnaval",
    items: [
      {
        title: "Promenade & Masséna fermées",
        description:
          "Corsi et batailles de fleurs ferment la Promenade des Anglais et la Place Masséna : le chauffeur connaît les déposes autorisées les plus proches des tribunes.",
      },
      {
        title: "Foule et stationnement saturé",
        description:
          "Le centre de Nice est dense pendant les défilés : on évite la galère de stationnement avec une dépose-reprise calée sur les horaires de parade.",
      },
      {
        title: "Familles & groupes",
        description:
          "Berlines et vans premium pour familles, enfants et groupes d'amis : un même chauffeur sur la journée, sans portage de poussettes dans la foule.",
      },
      {
        title: "Aéroport tout proche",
        description:
          "L'aéroport de Nice est à quelques minutes du centre : accueil, suivi de vol et transfert direct vers l'hôtel malgré l'affluence carnavalesque.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Carnaval de Nice",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison aéroport ↔ hôtel à Nice avec suivi de vol, en contournant les fermetures de la Promenade les soirs de défilé.",
      },
      {
        title: "Dépose près des défilés",
        text: "Approche au plus près de la Place Masséna et des tribunes selon le périmètre fermé et l'horaire des corsi.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée pour familles et groupes, entre hôtel, défilés et restaurants.",
      },
      {
        title: "Excursions autour du Carnaval",
        text: "Journées découverte vers Èze, Monaco, Cannes ou la fête des Citrons de Menton, souvent au même moment de l'année.",
      },
      {
        title: "Transferts hôtel & soirées",
        text: "Liaisons entre hôtels, dîners et illuminations du soir, avec retour après les parades nocturnes.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Carnaval",
    items: [
      {
        name: "Place Masséna",
        description:
          "Cœur du Carnaval : tribunes, char du Roi et corsi carnavalesques illuminés.",
      },
      {
        name: "Promenade des Anglais",
        description:
          "Batailles de fleurs en bord de mer, fermée à la circulation pendant les défilés.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description:
          "Terminaux 1 et 2, à quelques minutes du centre par la Promenade ou la voie Mathis.",
      },
      {
        name: "Vieux Nice & port",
        description: "Hôtels, restaurants et accès est au centre événementiel.",
      },
      {
        name: "Èze, Monaco & Menton",
        description:
          "Excursions de la Riviera, dont la fête des Citrons de Menton en même temps que le Carnaval.",
      },
    ],
  },
  generic: {
    heading: "Le Carnaval de Nice vu d'un chauffeur",
    subheading:
      "En février, Nice ferme la Promenade et la Place Masséna pour l'un des plus grands carnavals du monde — foule dense et circulation bouleversée.",
    paragraphs: [
      "Chaque mois de février, le Carnaval de Nice anime la ville pendant deux semaines : corsi carnavalesques sur la Place Masséna et batailles de fleurs sur la Promenade des Anglais. Pendant les défilés, ces axes sont fermés et le centre est saturé. Un chauffeur qui connaît les déposes autorisées vous rapproche des tribunes sans vous infliger la recherche de stationnement.",
      "L'aéroport de Nice Côte d'Azur est à quelques minutes du centre, mais les fermetures de la Promenade compliquent les liaisons les soirs de parade. Avec le suivi de vol et un itinéraire adapté (voie Mathis, accès est), nous assurons un transfert direct vers l'hôtel malgré l'affluence.",
      "Le Carnaval est un événement familial et grand public : berlines et vans premium pour familles, enfants et groupes d'amis, avec un même chauffeur sur la journée. Et comme février réunit aussi la fête des Citrons à Menton, nous combinons volontiers une excursion vers Menton, Monaco ou Èze.",
    ],
    bullets: [
      "Dépose au plus près des tribunes",
      "Itinéraires contournant la Promenade",
      "Familles, groupes & excursions",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Carnaval de Nice",
    steps: [
      "Indiquez vos dates, votre hôtel et les défilés (corsi, batailles de fleurs) que vous visez.",
      "Nous calons les itinéraires selon les fermetures de la Promenade et de la Place Masséna.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition pour défilés, dîners et excursions vers Menton, Monaco ou Èze.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pendant le Carnaval de Nice ?",
      a: "Réservez à l'avance : en février, le Carnaval attire une forte affluence sur Nice. Précisez vos dates, votre hôtel et les défilés visés pour que nous bloquions un chauffeur dédié, adapté aux familles comme aux groupes.",
    },
    {
      q: "Pouvez-vous me déposer près des défilés malgré les fermetures ?",
      a: "Oui. Pendant les corsi et les batailles de fleurs, la Place Masséna et la Promenade des Anglais sont fermées à la circulation. Le chauffeur vous dépose au point autorisé le plus proche des tribunes selon le périmètre et l'horaire du défilé, puis vous reprend à la sortie.",
    },
    {
      q: "Comment gérez-vous l'aéroport quand la Promenade est bloquée ?",
      a: "L'aéroport de Nice est tout proche du centre. Les soirs de parade, nous empruntons des itinéraires de contournement (voie Mathis, accès est) et suivons votre vol pour assurer un transfert direct vers l'hôtel malgré les fermetures.",
    },
    {
      q: "Proposez-vous des excursions autour du Carnaval ?",
      a: "Oui. Le Carnaval coïncide souvent avec la fête des Citrons de Menton : en mise à disposition, nous organisons des journées vers Menton, Monaco, Èze ou Cannes en complément des défilés niçois.",
    },
  ],
  relatedLinks: [
    {
      label: "Navette Aéroport de Nice Saint-Jean-Cap-Ferrat",
      href: "/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat",
    },
    {
      label: "Navette Monaco Aéroport de Nice",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Demander un devis",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Carnaval de Nice",
    text: "Transferts malgré les fermetures, dépose près des défilés et excursions : réservez un chauffeur dédié pour le Carnaval.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "Chauffeur Carnaval de Nice — VTC familles & Promenade | KS Rent Services",
    description:
      "Chauffeur privé pendant le Carnaval de Nice : transferts hôtel et aéroport malgré les fermetures de la Promenade, dépose près des défilés.",
    canonical: "/chauffeur/carnaval-de-nice",
  },
};

export default carnavalDeNice;
